import React from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useStyle } from 'react-native-style-utilities';
import type { AddressType } from '@sweetgreen/garnish';
import {
  ModalFooter,
  ScrollViewWithHeaderTitle,
  theme,
  useBreakpointHelpers,
  useFluidSize,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import { AppFooter, PageWrapper } from '@order/components';
import type { PartialLineItem } from '@order/graphql';
import type {
  CourierDetails,
  DeliveryPreferenceType,
  FlattenedOrderStatuses,
  Ledger,
} from '@order/graphql';
import { usePermanentNoticeBanner } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { OrderStatusContactUs } from './OrderStatus.contact';
import { OrderStatusDetails } from './OrderStatusDetails';

export const OrderStatusLayout = (props: OrderStatusScreenProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  useLateNotice(props.isLate);

  if (currentBreakpoint.isMobile) return <MobileOrderStatusScreen {...props} />;

  return <DesktopOrderStatusScreen {...props} />;
};

const DesktopOrderStatusScreen = ({
  info,
  children,
  ...rest
}: OrderStatusScreenProps) => {
  const fluid = useFluidSize({ min: 1024, max: 1440 });
  const width = fluid(376, 478);
  const widthStyle = useStyle(() => ({ width }), [width]);
  const orderStatusContainerStyles = [
    styles.orderStatusContainer,
    widthStyle,
    webOnlyStyles({ userSelect: 'auto' }),
  ];

  const desktopContentStyles = [
    styles.desktopContent,
    webOnlyStyles({ userSelect: 'auto' }),
  ];

  return (
    <PageWrapper contentContainerStyle={styles.pageWrapper}>
      <View
        testID="order-status.desktop.container"
        style={styles.desktopScreenContainer}
      >
        <View style={styles.desktopMapContainer}>{children}</View>
        <View style={orderStatusContainerStyles}>
          <View style={styles.infoContainer}>{info}</View>
          <View style={styles.divider} />
          <ScrollView style={desktopContentStyles}>
            <OrderStatusDetails {...rest} />
          </ScrollView>
          <OrderStatusContactUs />
        </View>
      </View>
    </PageWrapper>
  );
};

const MobileOrderStatusScreen = ({
  info,
  footer,
  children,
  ...rest
}: OrderStatusScreenProps) => {
  return (
    <>
      <ScrollViewWithHeaderTitle contentContainerStyle={styles.grow}>
        <View
          testID="order-status.mobile.container"
          style={styles.mobileScreenContainer}
        >
          <View style={styles.infoContent}>{info}</View>
          <View style={styles.mobileMapContainer}>{children}</View>
          <View style={styles.mobileDetails}>
            <OrderStatusDetails {...rest} />
            <OrderStatusContactUs />
          </View>
        </View>
        <AppFooter />
      </ScrollViewWithHeaderTitle>
      {footer ? (
        <ModalFooter style={styles.mobileDetails}>{footer}</ModalFooter>
      ) : null}
    </>
  );
};

const useLateNotice = (isLate?: boolean) => {
  const { t } = useLocalizationContext();
  const text = t('order-status-info.late');

  usePermanentNoticeBanner(Boolean(isLate), text);
};

type OrderStatusScreenProps = {
  readonly info: React.ReactNode;
  readonly footer: React.ReactNode;
  readonly children: React.ReactNode;
  readonly orderType: AddressType;
  readonly orderStatus: FlattenedOrderStatuses;
  readonly deliveryAddressName?: string;
  readonly deliveryAddressStreet?: string;
  readonly deliveryAddressSecondaryStreet?: string;
  readonly deliveryAddressCity?: string;
  readonly deliveryAddressState?: string;
  readonly deliveryAddressZipCode?: string;
  readonly deliveryAddressNotes?: string;
  readonly deliveryPreference?: DeliveryPreferenceType;
  readonly locationName?: string;
  readonly locationAddress?: string;
  readonly locationCity?: string;
  readonly locationState?: string;
  readonly locationZipCode?: string;
  readonly dropoffLocation?: string;
  readonly lineItems: readonly PartialLineItem[];
  readonly ledger: Partial<Ledger>;
  readonly courierName?: CourierDetails['name'];
  readonly courierPhone?: CourierDetails['phone'];
  readonly isLate?: boolean;
};

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
  },
  pageWrapper: {
    height: '100%',
  },
  desktopScreenContainer: {
    flexDirection: 'row',
    height: '100%',
    backgroundColor: theme.colors.KHAKI_3,
  },
  mobileScreenContainer: {
    overflow: 'hidden',
    backgroundColor: theme.colors.KHAKI_3,
    marginBottom: theme.spacing.medium,
  },
  mobileMapContainer: {
    position: 'relative',
    height: 300,
    borderTopColor: theme.colors.NEUTRAL_4,
    borderBottomColor: theme.colors.NEUTRAL_4,
    marginBottom: theme.spacing.medium,
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  desktopMapContainer: {
    position: 'relative',
    flex: 1,
  },
  orderStatusContainer: {
    borderLeftColor: theme.colors.NEUTRAL_1,
    borderLeftWidth: 1,
  },
  desktopContent: {
    padding: theme.spacing.xlarge,
  },
  infoContent: {
    flex: 1,
    paddingTop: theme.spacing.small,
    paddingHorizontal: theme.spacing.medium,
    paddingBottom: theme.spacing.xlarge,
  },
  mobileDetails: {
    borderTopColor: theme.colors.DARK_KALE,
    paddingHorizontal: theme.spacing.medium,
  },
  infoContainer: {
    padding: theme.spacing.xlarge,
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: theme.colors.GREEN_1,
  },
});
